/* eslint-disable max-lines */
import { NgTemplateOutlet } from '@angular/common';
import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';

import { select, Store } from '@ngrx/store';
import moment from 'moment-timezone';
import { BehaviorSubject, merge as observableMerge, Subject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { AdminsSelectDialogComponent } from '@core/account-smart-ui';
import type { CurrentUser, User } from '@core/account-types';
import type { Candidate } from '@core/candidate-types';
import { filterBoolean } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { TimePickerComponent } from '@core/time-ui';
import {
  AdminsAvatarsComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  ConnectedOverlayDirective,
  DialogService,
  DialogWidth,
  DividerComponent,
  DropdownComponent,
  GroupComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  OverlayOriginDirective,
  ProtectedControlDirective,
  RtStatefulComponent,
} from '@core/user-interface-legacy';

export interface TaskFormChangeEvent {
  title: string;
  dueDate: string | null;
  timezone: string | null;
  candidateId: number | null;
  adminIds: number[];
}

type State = {
  timeData: { date: string; time: string; zone: string } | null;
  selectedAdmins: CurrentUser[];
  keepFocus: boolean;
};

@Component({
  selector: 'rt-task-form',
  templateUrl: './task-form.component.html',
  styleUrls: ['./task-form.component.less'],
  imports: [
    InputComponent,
    ReactiveFormsModule,
    ProtectedControlDirective,
    DividerComponent,
    ButtonComponent,
    OverlayOriginDirective,
    IconComponent,
    IconButtonComponent,
    ConnectedOverlayDirective,
    DropdownComponent,
    TimePickerComponent,
    AdminsAvatarsComponent,
    NgTemplateOutlet,
    GroupComponent,
    ButtonLoadingDirective,
    TranslatePipe,
  ],
})
export class TaskFormComponent extends RtStatefulComponent<State> {
  @Input() public candidate: Candidate;
  @Input() public currentAdmin: CurrentUser;
  @Input() public assignAdmin: boolean = false;
  @Input() public taskLoading: boolean = false;
  @Input() public alwaysActive: boolean = false;
  @Input() public actionsVisible: boolean = true;
  @Input() public focusOnStart: boolean = false;
  @Input() public allowedUsers: User[];
  @Input() public disallowedUsers: User[];
  @Input() public hasError: boolean = false;
  @Input() public withNewStyling: boolean = false;
  @Input() public placeholder: string;
  @ViewChild('textInput') public textInput: InputComponent;

  public taskControl: UntypedFormControl;
  public canViewCandidates: boolean;
  public active$ = new BehaviorSubject(false);
  public isTrulyActive = false;
  public showActionsOutsideContainer = false;

  public taskSubmit = output<TaskFormChangeEvent>();
  public taskChange = output<TaskFormChangeEvent>();
  public formActive = output<boolean>();

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private dialogService: DialogService,
    private viewContainerRef: ViewContainerRef,
    private destroyRef: DestroyRef,
    private store: Store,
    protected cdRef: ChangeDetectorRef,
  ) {
    super(cdRef);
    this.createState({
      timeData: null,
      selectedAdmins: [],
      keepFocus: false,
    });
    this.taskControl = new UntypedFormControl('', Validators.required);
  }

  public ngOnInit(): void {
    observableMerge(this.taskControl.valueChanges, this.taskControl.statusChanges)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => this.cdRef.detectChanges());

    this.setState({
      ...this.state,
      selectedAdmins: this.currentAdmin && this.assignAdmin ? [this.currentAdmin] : [],
    });

    this.taskControl.valueChanges
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()), skip(1))
      .subscribe(title => this.emitTaskChange(title));

    this.emitTaskChange();
    this.checkAbilities();
    this.active$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(active => {
      this.setActionsVisibilityState(active);
    });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { assignAdmin, taskLoading, hasError } = changes;

    if (assignAdmin) {
      if (assignAdmin.currentValue && !this.state.selectedAdmins.length) {
        this.setState(
          {
            ...this.state,
            selectedAdmins: [this.currentAdmin],
          },
          false,
        );
      } else if (!assignAdmin.currentValue && this.isAssigned()) {
        this.setState(
          {
            ...this.state,
            selectedAdmins: [],
          },
          false,
        );
      }
    }

    if (
      (taskLoading &&
        taskLoading.previousValue !== taskLoading.currentValue &&
        taskLoading.currentValue === false) ||
      hasError?.currentValue === false
    ) {
      this.cancel();
    }
  }

  public onFocus(): void {
    this.active$.next(true);
  }

  public onBlur(): void {
    if (this.alwaysActive) {
      return;
    }

    if (this.taskControl.value === '' && !this.state.keepFocus && !this.taskLoading) {
      this.cancel();
    } else {
      this.setState({
        ...this.state,
        keepFocus: false,
      });
    }
  }

  public setKeepFocus(): void {
    this.setState({
      ...this.state,
      keepFocus: true,
    });
  }

  public clearTimeData(): void {
    this.setState({
      ...this.state,
      timeData: null,
    });
  }

  public editAssignments(): void {
    const data = {
      assignments: (this.state.selectedAdmins || []).map(admin => admin.id),
      ...(this.allowedUsers ? { allowedUsers: this.allowedUsers } : {}),
      ...(this.disallowedUsers ? { disallowedUsers: this.disallowedUsers } : {}),
    };
    const dialog = this.dialogService.custom(
      AdminsSelectDialogComponent,
      { width: DialogWidth.SmallX },
      data,
      this.viewContainerRef,
    );
    dialog.afterClosed.pipe(filterBoolean).subscribe(result => {
      if (result.users) {
        this.setState({
          ...this.state,
          selectedAdmins: result.users,
        });
        this.emitTaskChange();
      }
    });
  }

  public onTimeChange(timeData: { date: string; time: string; zone: string }): void {
    this.setState({
      ...this.state,
      timeData: {
        ...timeData,
        time: moment(timeData.time, 'LT').format('HH:mm'),
      },
    });
    this.emitTaskChange();
  }

  public getTimeDataString(): string | null {
    return this.state.timeData
      ? moment
          .tz(
            `${this.state.timeData.date}T${this.state.timeData.time}`,
            'YYYY-MM-DDTHH:mm',
            this.state.timeData.zone,
          )
          .format('ll LT')
      : null;
  }

  public cancel(): void {
    this.taskControl.reset('');
    this.taskControl.setErrors(null);
    this.active$.next(false);
    this.setState({
      ...this.state,
      selectedAdmins: this.currentAdmin ? [this.currentAdmin] : [],
      timeData: null,
      keepFocus: false,
    });
  }

  public submit(): void {
    if (this.taskControl.invalid || !this.taskControl.value.trim().length) {
      return;
    }

    const task = {
      title: this.taskControl.value,
      dueDate: this.getDateToSubmit(),
      timezone: this.state.timeData ? this.state.timeData.zone : null,
      candidateId: this.candidate ? this.candidate.id : null,
      adminIds: (this.state.selectedAdmins || []).map(admin => admin.id),
    };
    this.taskSubmit.emit(task);
  }

  private getDateToSubmit(): string | null {
    return this.state.timeData
      ? moment
          .tz(
            `${this.state.timeData.date}T${this.state.timeData.time}`,
            'YYYY-MM-DDTHH:mm',
            this.state.timeData.zone,
          )
          .utc()
          .format()
      : null;
  }

  private isAssigned(): boolean {
    const firstAdminId = (this.state.selectedAdmins || []).map(admin => admin.id)[0];
    return this.state.selectedAdmins.length === 1 && firstAdminId === this.currentAdmin.id;
  }

  private emitTaskChange(taskTitle: string = this.taskControl.value): void {
    this.taskChange.emit({
      title: taskTitle,
      dueDate: this.getDateToSubmit(),
      timezone: this.state.timeData ? this.state.timeData.zone : null,
      candidateId: this.candidate ? this.candidate.id : null,
      adminIds: Array.isArray(this.state.selectedAdmins)
        ? this.state.selectedAdmins.map(admin => admin.id)
        : [],
    });
  }

  private checkAbilities(): void {
    this.store
      .pipe(
        select(AbilitiesSelectors.hasOne(Ability.ViewCandidates)),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(canViewCandidates => {
        this.canViewCandidates = canViewCandidates;
        this.cdRef.detectChanges();

        if (this.focusOnStart && canViewCandidates) {
          this.textInput.setFocus();
        }
      });
  }

  private setActionsVisibilityState(active: boolean): void {
    this.isTrulyActive = this.alwaysActive || active;
    this.showActionsOutsideContainer =
      this.isTrulyActive && this.actionsVisible && this.withNewStyling;
    this.formActive.emit(this.isTrulyActive);
    this.cdRef.detectChanges();
  }
}
